import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { StorageManager } from '@atlas/core';
import { AdvancedHistoryFilter, ChipsItem, Label, Select } from '@atlas/ui';

import { transactionStatuses } from 'common/constants';
import DateRangePicker from 'common/DateRangePicker/DateRangePicker';
import useTranslate from 'common/I18n/Interpreter';
import { deleteSearchParam } from 'src/helpers/deleteSearchParam';
import { setSearchParam } from 'src/helpers/setSearchParam';

const FILTER_STORAGE_KEY = 'PaymentHistoryFilterHidden';

export const getPaymentOperations = defaultOption => [
  defaultOption,
  {
    name: 'deposit',
    value: 'DEPOSIT',
  },
  {
    name: 'withdrawal',
    value: 'WITHDRAWAL',
  },
];

const getPaymentStatuses = defaultOption => [
  defaultOption,
  {
    name: 'payment_history_processing',
    value: transactionStatuses.PENDING,
  },
  {
    name: 'payment_history_completed',
    value: transactionStatuses.COMPLETED,
  },
  {
    name: 'payment_history_failed',
    value: transactionStatuses.FAILED,
  },
];

export const TopFilters = ({
  DEFAULT_OPTION,
  operation,
  paymentOperations,
  typesChangeHandler,
}) => {
  const history = useHistory();

  return paymentOperations.map(option => {
    const active = (!operation && option.value === DEFAULT_OPTION) || option.value === operation;

    const onHandleChips = () => {
      if (active) return;
      typesChangeHandler(option.value);

      let newUrl;
      if (option.value === DEFAULT_OPTION) {
        newUrl = deleteSearchParam(history.location.search, ['operation']);
      } else {
        newUrl = setSearchParam(history.location.search, 'operation', option.name);
      }

      history.replace({
        search: newUrl,
      });
    };

    return (
      <ChipsItem
        active={active}
        key={option.name}
        label={<Label message={option.name} />}
        onClick={onHandleChips}
      />
    );
  });
};

const TransactionHistoryFilter = ({
  DEFAULT_OPTION,
  applyHandler,
  customOperationChangeHandler,
  filter,
  isDisabledApplyBtn,
  isDisabledResetBtn,
  onDateChange,
  paymentMethodChangeHandler,
  paymentMethods: fetchedPaymentMethods,
  paymentOperations,
  resetHandler,
  statusChangeHandler,
  typesChangeHandler,
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const t = useTranslate();

  useEffect(() => {
    setPaymentMethods(
      fetchedPaymentMethods.map(i => ({
        text: i.paymentMethodName,
        value: i.id,
      })),
    );
  }, [fetchedPaymentMethods]);

  const selectOptions = useMemo(
    () => ({
      operations: paymentOperations.map(option => ({
        text: t(option.name),
        value: option.value,
      })),
      paymentMethods: [
        {
          text: t(DEFAULT_OPTION),
          value: DEFAULT_OPTION,
        },
        ...paymentMethods,
      ],
      statuses: getPaymentStatuses({
        name: DEFAULT_OPTION,
        value: DEFAULT_OPTION,
      }).map(option => ({
        text: t(option.name),
        value: option.value,
      })),
    }),
    [DEFAULT_OPTION, t, paymentOperations, paymentMethods],
  );

  const isInitialShowing = useMemo(() => StorageManager.getItem(FILTER_STORAGE_KEY), []);

  const calendarDateRange = {
    from: dayjs(filter.createDate.from),
    to: dayjs(filter.createDate.to),
  };

  const setInitialShowing = useCallback(newValue => {
    if (newValue) {
      StorageManager.setItem(FILTER_STORAGE_KEY, '1');
    } else {
      StorageManager.removeItem(FILTER_STORAGE_KEY);
    }
  }, []);

  const topFilters = (
    <TopFilters
      DEFAULT_OPTION={DEFAULT_OPTION}
      operation={filter.operation}
      paymentOperations={paymentOperations}
      typesChangeHandler={typesChangeHandler}
    />
  );

  return (
    <>
      <AdvancedHistoryFilter
        applyHandler={applyHandler}
        isDisabledApplyBtn={isDisabledApplyBtn}
        isDisabledResetBtn={isDisabledResetBtn}
        isInitialShowing={isInitialShowing}
        isMobile={true}
        resetHandler={resetHandler}
        setInitialShowing={setInitialShowing}
        topFilters={topFilters}
      >
        <div className="controls-bar__inner controls-bar__inner-filters">
          <div className="payments-filter__field">
            <Label className="payments-filter__label" message="payment_history_select_date_range" />
            <DateRangePicker
              calendarDateRange={calendarDateRange}
              setCalendarDateRange={onDateChange}
            />
          </div>
          {!!paymentMethods.length && (
            <div className="payments-filter__field">
              <Select
                className="payments-filter__select"
                label="payment_history_select_payment_method"
                onChange={paymentMethodChangeHandler}
                options={selectOptions.paymentMethods}
                value={+filter.pmId ? +filter.pmId : DEFAULT_OPTION}
              />
            </div>
          )}
          {!filter.operation && (
            <div className="payments-filter__field">
              <Select
                className="payments-filter__select"
                label="payment_history_operation_type"
                onChange={customOperationChangeHandler}
                options={selectOptions.operations}
                value={filter.customOperation || DEFAULT_OPTION}
              />
            </div>
          )}
          <div className="payments-filter__field">
            <Select
              className="payments-filter__select"
              label="payment_history_status"
              onChange={statusChangeHandler}
              options={selectOptions.statuses}
              value={filter.status || DEFAULT_OPTION}
            />
          </div>
        </div>
      </AdvancedHistoryFilter>
    </>
  );
};

TransactionHistoryFilter.propTypes = {
  DEFAULT_OPTION: PropTypes.string,
  applyHandler: PropTypes.func,
  customOperationChangeHandler: PropTypes.func,
  filter: PropTypes.shape({
    createDate: PropTypes.object,
    customOperation: PropTypes.string,
    operation: PropTypes.string,
    pmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
  }),
  isDisabledApplyBtn: PropTypes.bool,
  isDisabledResetBtn: PropTypes.bool,
  onDateChange: PropTypes.func,
  paymentMethodChangeHandler: PropTypes.func,
  paymentMethods: PropTypes.arrayOf(PropTypes.object),
  paymentOperations: PropTypes.arrayOf(PropTypes.object),
  resetHandler: PropTypes.func,
  statusChangeHandler: PropTypes.func,
  typesChangeHandler: PropTypes.func,
};

export default TransactionHistoryFilter;
